import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Card / Panel — the surface language of PRD §10.2: white, `border-slate-200`,
 * `rounded`, `shadow-sm`. Card is the free-flowing surface; Panel is the one
 * that fills its parent and scrolls internally (DS-01 — the page never scrolls,
 * regions do), which is what every master/detail screen is built out of.
 */

export type CardProps = HTMLAttributes<HTMLDivElement>;

export const Card = forwardRef<HTMLDivElement, CardProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('rounded border border-slate-200 bg-white shadow-sm', className)}
    {...props}
  />
));
Card.displayName = 'Card';

export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Arabic title of the card. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Buttons and filters; laid out on the end side (start side in LTR). */
  actions?: ReactNode;
  icon?: LucideIcon;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, actions, icon: Icon, className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // NFR-05 — on a phone the actions break under the title instead of
        // squeezing it: a card header holding two buttons and a name has no
        // 375px arrangement where all three fit on one line.
        'flex shrink-0 items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2',
        'max-lg:flex-wrap max-lg:gap-y-2',
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 shrink-0 text-slate-400" /> : null}
        <div className="min-w-0">
          <h3 className="truncate text-xs font-bold text-slate-700">{title}</h3>
          {subtitle ? <p className="truncate text-[11px] text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      {actions ? (
        <div className="flex shrink-0 items-center gap-1.5 max-lg:flex-wrap max-lg:[&>*]:flex-wrap">{actions}</div>
      ) : null}
    </div>
  ),
);
CardHeader.displayName = 'CardHeader';

export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  /** Drop the padding so a table or a list can sit flush against the frame. */
  noPadding?: boolean;
}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ noPadding, className, ...props }, ref) => (
    // NFR-05 — a phone spends 12px a side on chrome it cannot afford.
    <div ref={ref} className={cn(!noPadding && 'p-3 max-lg:p-2.5', className)} {...props} />
  ),
);
CardBody.displayName = 'CardBody';

export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Omit to get a headerless panel — a bare scroll surface. */
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  icon?: LucideIcon;
  /** Pinned strip under the scroll region — totals, pagination, a save bar. */
  footer?: ReactNode;
  noPadding?: boolean;
  /** Classes for the scroll region itself, not the frame. */
  bodyClassName?: string;
}

/**
 * DS-01 — a Card whose body is the scroll region. The panel takes the height it
 * is given (`h-full` inside a flex/grid cell) and never lets its content push
 * the page taller, so master/detail layouts fill the viewport exactly once.
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(
  (
    { title, subtitle, actions, icon, footer, noPadding, bodyClassName, className, children, ...props },
    ref,
  ) => (
    <div
      ref={ref}
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded border border-slate-200 bg-white shadow-sm',
        className,
      )}
      {...props}
    >
      {title !== undefined ? (
        <CardHeader title={title} subtitle={subtitle} actions={actions} icon={icon} />
      ) : null}
      <div
        className={cn(
          'lg:touch-scroll min-h-0 flex-1 overflow-auto',
          !noPadding && 'p-3 max-lg:p-2.5',
          bodyClassName,
        )}
      >
        {children}
      </div>
      {footer ? (
        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-3 py-2">{footer}</div>
      ) : null}
    </div>
  ),
);
Panel.displayName = 'Panel';
